import { html, type Html, Page, View } from 'rune-ts';
import klass from './PlusShopCreator.module.scss';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { footer_links } from '../../components/cells/Footer/constant';
import { CreatorCard, CreatorLikeToggleEvent } from '../../components/cells/CreatorCard/CreatorCard';
import { Title } from '../../components/atoms/Title/Title';
import { CreatorApiS, CreatorTypeS } from '../../../features/Creator/outbound/share';
import { CreatorEmptyScreen } from '../../components/cells/CreatorEmptyScreen/CreatorEmptyScreen';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { SubHeaderMobile } from '../../components/cells/Header/Global/views/SubHeaderMobile.ts';
import { Dropdown, DropdownChangeEvent } from '../../components/cells/Dropdown/Dropdown';
import { CreatorsAssociateOrderBy } from '../../../features/Creator/type';
import { loading, loadingEnd } from '../../../shared/client/global';

interface CateItem {
  id: number;
  name: string;
}

interface CateList {
  id: number;
  name: string;
  name_en: string;
  _: {
    cate_items: CateItem[];
  };
}

type PlusCrew = {
  id: number;
  domain_name: string;
  name: string;
};

export type PlusShopCreatorPageData = {
  title: string;
  plus_crew: PlusCrew;
  cate_lists: CateList[];
  creators: CreatorTypeS.CreatorCardType[];
  layout_options: BaseLayoutOptions;
};

const text = {
  recommendation: ET('mps2::creator::plus::filter::recommendation'),
  new: ET('mps2::creator::plus::filter::new'),
  popular: ET('mps2::creator::plus::filter::popular'),
  like: ET('mps2::creator::plus::filter::like'),
  review: ET('mps2::creator::plus::filter::review'),
};

class PlusShopCreatorPageView extends View<PlusShopCreatorPageData> {
  plusCreatorDropdown: Dropdown<CreatorsAssociateOrderBy>;

  constructor(data: PlusShopCreatorPageData) {
    super(data);
    const is_mobile = data.layout_options.is_mobile;
    this.plusCreatorDropdown = new Dropdown<CreatorsAssociateOrderBy>(
      {
        options: [
          { name: text.recommendation, value: 'RECOMMENDATION', key: 'RECOMMENDATION' },
          { name: text.new, value: 'NEW', key: 'NEW' },
          { name: text.popular, value: 'POPULAR', key: 'POPULAR' },
          { name: text.like, value: 'LIKE', key: 'LIKE' },
          { name: text.review, value: 'REVIEW', key: 'REVIEW' },
        ],
        selected_option_key: 'RECOMMENDATION',
      },
      {
        horizontal: 'right',
        without_container: is_mobile,
        fit_width_to_longest_name: true,
        text_align: is_mobile ? 'right' : 'left',
      },
    );
  }

  protected override template(data: PlusShopCreatorPageData): Html {
    const empty_title = ET('mps2::creator::empty_title');

    return html`
      <div class="${klass.wrap}">
        <div class="${klass.title_wrapper}">
          <div class="${klass.title}">
            ${new Title({
              title: data.title,
            })}
          </div>
          <div class="${klass.control_panel}">${this.plusCreatorDropdown}</div>
        </div>
        ${data.creators.length
          ? html`
              <div class="${klass.creators}">
                ${data.creators.map((creatorCardData) => new CreatorCard(creatorCardData))}
              </div>
            `
          : html`
              ${new CreatorEmptyScreen({
                title: empty_title,
                body: `${data.plus_crew.name || 'Plus Shop'}`,
              })}
            `}
      </div>
    `;
  }

  override onRender() {
    this.addEventListener(DropdownChangeEvent<CreatorsAssociateOrderBy>, async (e) => {
      const order_by = e.detail.option?.value || 'RECOMMENDATION';
      await this.refreshCreatorList(order_by);
    });
  }

  async refreshCreatorList(order_by: CreatorsAssociateOrderBy) {
    loading();
    this.data.creators = await CreatorApiS.getPlusCreatorsApi({
      order_by,
    });
    this.redraw();
    loadingEnd();
  }
}

export class PlusShopCreatorPage extends Page<PlusShopCreatorPageData> {
  protected override template(data: PlusShopCreatorPageData): Html {
    const { layout_options } = data;

    return html`
      <div class="${klass.page}">
        ${new LayoutView(
          {
            body: new PlusShopCreatorPageView(data),
            footer_data: { ...footer_links, lang: ET.lang, is_mobile: layout_options.is_mobile },
          },
          {
            has_bottom_tab_bar: true,
            selected_bottom_tab_idx: -1,
            ...layout_options,
          },
        )}
      </div>
    `;
  }

  override onRender() {
    SubHeaderMobile.handleChangeMenuOutside();
    this.delegate(CreatorLikeToggleEvent, CreatorCard, async (e) => {
      e.detail.toggleLikeButton();
    });
  }
}
